import { useEffect, useState, useCallback } from 'react'
import { Card, Badge, Button, SectionHeading, Divider, KVRow, Modal } from '../../../shared/components/UI'
import { mmApi } from '../../../shared/lib/api'
import { History, Layers, Eye, Calendar, User, Package, Clock, PlayCircle } from 'lucide-react'

const FALLBACK = '-'

// Helper to format duration
function getDuration(updatedAt) {
  const start = new Date(updatedAt)
  const now = new Date()
  const diffMs = now - start
  
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  
  if (diffHrs > 0) return `${diffHrs}h ${diffMins}m`
  return `${diffMins}m`
}

export default function InitializedRollsPage() {
  const [loading, setLoading] = useState(true)
  const [rolls, setRolls]     = useState([])
  const [total, setTotal]     = useState(0)
  const [filters, setFilters] = useState({ page: 1 })
  const [pages, setPages]     = useState(1)
  const [now, setNow]         = useState(new Date())

  // Tick every minute to update durations
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await mmApi.listInitiatedRolls(filters)
      const { rows, count, pages: pCount } = res.data.data
      setRolls(rows)
      setTotal(count)
      setPages(pCount)
    } catch (err) {
      console.error('Error loading initiated rolls:', err)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => { loadData() }, [loadData])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800 tracking-tight">Initialized Rolls (MM)</h2>
          <p className="text-xs text-gray-400 mt-0.5 font-medium">Tracking rolls initiated by Metallic Master for batching</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="purple" className="px-3 py-1 font-black shadow-sm ring-1 ring-purple-100">{total} Active</Badge>
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <Button variant="secondary" size="sm" onClick={loadData} className="rounded-xl border-gray-100 hover:bg-white shadow-sm transition-all">
            <History size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Main Table */}
      <Card bodyClass="!p-0 overflow-hidden ring-1 ring-gray-100/50 shadow-sm rounded-2xl">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="w-[180px]">Roll Code</th>
                <th>Shade</th>
                <th>Unit</th>
                <th>Available Weight</th>
                <th>Initiated At</th>
                <th>Duration</th>
                <th className="text-center w-[120px]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="7" className="py-24 text-center text-gray-400 font-medium italic">Scanning for initiated rolls...</td></tr>
              ) : rolls.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-32 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-300">
                        <PlayCircle size={24} />
                      </div>
                      <p className="text-sm font-medium text-gray-400">No rolls currently in initiated state.</p>
                      <p className="text-[10px] text-gray-300 uppercase tracking-widest font-bold">Use MM App to initialize</p>
                    </div>
                  </td>
                </tr>
              ) : rolls.map(roll => {
                const duration = getDuration(roll.updated_at)
                const isOverdue = duration.includes('h') && parseInt(duration) >= 4 // Highlight if > 4 hours
                
                return (
                  <tr key={roll.id} className="group hover:bg-purple-50/20 transition-colors">
                    <td className="font-black text-purple-700 font-mono tracking-tight text-sm py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(147,51,234,0.5)]" />
                        {roll.roll_code}
                      </div>
                    </td>
                    <td className="text-sm">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-700">{roll.shade?.name || FALLBACK}</span>
                        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-tighter">{roll.shade?.code || ''}</span>
                      </div>
                    </td>
                    <td className="text-xs font-bold text-gray-500">
                      {roll.unit?.name || FALLBACK}
                    </td>
                    <td className="text-sm font-black text-gray-800">
                      {parseFloat(roll.available_weight_kg).toFixed(3)} 
                      <span className="text-[10px] text-gray-400 font-medium ml-1">KG</span>
                    </td>
                    <td className="text-xs text-gray-500 font-medium whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="text-gray-300" />
                        <span>{new Date(roll.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span className="text-gray-300 mx-1">·</span>
                        <span>{new Date(roll.updated_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
                      </div>
                    </td>
                    <td>
                      <div className={`flex items-center gap-1.5 text-xs font-black ${isOverdue ? 'text-amber-600' : 'text-gray-400'}`}>
                        <Clock size={12} className={isOverdue ? 'animate-pulse' : ''} />
                        {duration}
                      </div>
                    </td>
                    <td className="text-center">
                      <Badge variant="purple" className="uppercase text-[9px] tracking-widest font-black ring-1 ring-purple-100">
                        {roll.status}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-between bg-gray-50/30">
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest leading-none">
              Page {filters.page} of {pages} <span className="mx-2 text-gray-200">/</span> {total} Rolls
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === 1}
                onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
                className="rounded-lg px-4"
              >Previous</Button>
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === pages}
                onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
                className="rounded-lg px-4"
              >Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
